// CommandPalette (DESIGN §3.1.8, UX G4): cmdk in einem Radix-Dialog, 640 breit bei 15 % der Höhe,
// mobil Vollbild. Statische Befehle (Registry `commands`) plus Suchanbieter (`commandProviders`),
// entprellt 120 ms, höchstens 5 Treffer je Gruppe. Leer: „Recent“ + „Suggested actions“.
import { ArrowSquareOut, MagnifyingGlass, Sparkle } from '@phosphor-icons/react';
import { Command } from 'cmdk';
import { Dialog } from 'radix-ui';
import { useMemo, useRef, useState } from 'react';
import { cn } from '../kern/cn';
import { Kbd, VisuallyHidden } from '../kern/Misc';
import {
  filterCommands,
  groupBy,
  type PaletteCommand,
  type PaletteProvider,
  type PaletteResult,
  readRecent,
  rememberRecent,
  useProviderResults,
} from './command-palette.logic';

export type { PaletteCommand, PaletteProvider, PaletteResult } from './command-palette.logic';
export { filterCommands, useCommandPaletteShortcut } from './command-palette.logic';

export interface NavigateOptions {
  /** Volle Navigation (andere Plattform). */
  reload?: boolean;
  /** ⌘/Ctrl Enter: im Dock bzw. in Bar AI öffnen. */
  modifier?: boolean;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: readonly PaletteCommand[];
  providers?: readonly PaletteProvider[];
  onNavigate: (href: string, options: NavigateOptions) => void;
  /** „Ask Bar AI: ‚…‘“ bei leerer Trefferliste. */
  onAskAi?: (query: string) => void;
  placeholder?: string;
  /** Schlüssel für „Recent“ in localStorage (je Plattform). */
  recentKey?: string;
}

const itemClass =
  'flex h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-body text-fg outline-none select-none data-[selected=true]:bg-tile-hover';

function Row({
  label,
  description,
  icon: Glyph,
  leading,
  shortcut,
  reload,
}: Omit<PaletteCommand, 'id' | 'group'>) {
  return (
    <>
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-icon-circle text-fg-secondary">
        {leading ?? (Glyph ? <Glyph size={16} aria-hidden /> : null)}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{label}</span>
        {description ? (
          <span className="truncate text-label text-fg-secondary">{description}</span>
        ) : null}
      </span>
      {shortcut ? <Kbd>{shortcut}</Kbd> : null}
      {reload ? (
        <ArrowSquareOut
          size={16}
          aria-label="Opens another platform"
          className="text-fg-tertiary"
        />
      ) : null}
    </>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1].map((i) => (
        <div key={i} aria-hidden className="flex h-12 items-center gap-3 px-3">
          <span className="size-7 animate-skeleton rounded-full bg-skeleton" />
          <span className="h-3 w-40 animate-skeleton rounded-full bg-skeleton" />
        </div>
      ))}
    </>
  );
}

export function CommandPalette({
  open,
  onOpenChange,
  commands,
  providers = [],
  onNavigate,
  onAskAi,
  placeholder = 'Search people, goals, apps, pages…',
  recentKey = 'bar.palette.recent',
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const modifier = useRef(false);
  const results = useProviderResults(providers, open ? query : '');
  const [recent, setRecent] = useState<PaletteCommand[]>(() => readRecent(recentKey));
  const close = (next: boolean) => {
    if (!next) setQuery('');
    onOpenChange(next);
  };

  const trimmed = query.trim();
  const staticGroups = useMemo(() => {
    if (!trimmed) {
      const suggested = commands.filter((c) => c.group === 'Actions').slice(0, 5);
      return [
        ...(recent.length ? [{ group: 'Recent', items: recent }] : []),
        ...(suggested.length ? [{ group: 'Suggested actions', items: suggested }] : []),
      ];
    }
    return groupBy(filterCommands(commands, trimmed), (c) => c.group).map(([group, items]) => ({
      group,
      items: items.slice(0, 5),
    }));
  }, [commands, recent, trimmed]);

  const select = (item: PaletteCommand | (PaletteResult & { group: string })) => {
    rememberRecent(recentKey, item);
    setRecent(readRecent(recentKey));
    close(false);
    if ('perform' in item && item.perform) item.perform();
    else if (item.href) onNavigate(item.href, { reload: item.reload, modifier: modifier.current });
  };

  const providerGroups = trimmed ? [...providers].sort((a, b) => a.order - b.order) : [];
  const anyLoading = providerGroups.some((p) => results[p.id]?.status === 'loading');
  const providerHits = providerGroups.reduce((n, p) => n + (results[p.id]?.items.length ?? 0), 0);
  const nothing =
    Boolean(trimmed) && !anyLoading && providerHits === 0 && staticGroups.length === 0;

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-(--bar-z-dialog) bg-scrim data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-(--bar-z-dialog) flex flex-col overflow-hidden bg-overlay shadow-3 outline-none data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out md:inset-auto md:top-[15%] md:left-1/2 md:max-h-[70dvh] md:w-[min(640px,calc(100vw-32px))] md:-translate-x-1/2 md:rounded-2xl"
          onKeyDown={(event) => {
            modifier.current = event.metaKey || event.ctrlKey;
          }}
        >
          <VisuallyHidden>
            <Dialog.Title>Search</Dialog.Title>
          </VisuallyHidden>
          <Command
            shouldFilter={false}
            loop
            label="Search"
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex h-14 shrink-0 items-center gap-3 border-b border-line-subtle px-4">
              <MagnifyingGlass size={20} aria-hidden className="shrink-0 text-fg-secondary" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder={placeholder}
                className="h-full min-w-0 flex-1 bg-transparent text-body text-fg outline-none placeholder:text-fg-placeholder"
              />
              <Dialog.Close className="rounded-xs" aria-label="Close">
                <Kbd>Esc</Kbd>
              </Dialog.Close>
            </div>
            <Command.List className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 md:max-h-[min(60dvh,480px)]">
              {staticGroups.map(({ group, items }) => (
                <Command.Group
                  key={group}
                  heading={group}
                  className="pb-1 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-chip [&_[cmdk-group-heading]]:text-fg-tertiary"
                >
                  {items.map((item) => (
                    <Command.Item
                      key={`${group}:${item.id}`}
                      value={`${group}:${item.id}`}
                      className={itemClass}
                      onSelect={() => select(item)}
                    >
                      <Row {...item} />
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
              {providerGroups.map((provider) => {
                const state = results[provider.id];
                if (!state || (state.status === 'done' && state.items.length === 0)) return null;
                return (
                  <Command.Group
                    key={provider.id}
                    heading={
                      state.status === 'error' ? `${provider.group} · Unavailable` : provider.group
                    }
                    className="pb-1 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-chip [&_[cmdk-group-heading]]:text-fg-tertiary"
                  >
                    {state.status === 'loading' ? <SkeletonRows /> : null}
                    {state.items.slice(0, 5).map((item) => (
                      <Command.Item
                        key={`${provider.id}:${item.id}`}
                        value={`${provider.id}:${item.id}`}
                        className={itemClass}
                        onSelect={() => select({ ...item, group: provider.group })}
                      >
                        <Row {...item} />
                      </Command.Item>
                    ))}
                  </Command.Group>
                );
              })}
              {nothing ? (
                <div className="px-3 py-6 text-center text-body text-fg-secondary" role="status">
                  No results for ‘{trimmed}’
                </div>
              ) : null}
              {trimmed && onAskAi ? (
                <Command.Group
                  heading="Bar AI"
                  className={cn('pb-1 [&_[cmdk-group-heading]]:sr-only')}
                >
                  <Command.Item
                    value="ask-ai"
                    className={itemClass}
                    onSelect={() => {
                      close(false);
                      onAskAi(trimmed);
                    }}
                  >
                    <Row label={`Ask Bar AI: ‘${trimmed}’`} icon={Sparkle} />
                  </Command.Item>
                </Command.Group>
              ) : null}
            </Command.List>
            <div className="hidden h-9 shrink-0 items-center gap-4 border-t border-line-subtle px-4 text-small text-fg-tertiary md:flex">
              <span>↵ open</span>
              <span>⌘↵ open in dock</span>
              <span>esc close</span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
